import { useEffect, useState } from "react";
import { RefreshCw, Loader2, CloudOff, Cloud, FolderKanban, Camera, ListChecks, CheckCircle2 } from "lucide-react";
import { useField } from "../FieldContext";
import { Mono, Btn } from "../ui";

export default function SyncQueue() {
  const { store, online, syncing, sync, version, lastSync } = useField();
  const [q, setQ] = useState({ assets: [], photos: [], ops: [] });

  useEffect(() => {
    store.pendingList().then(setQ);
  }, [store, version, syncing]);

  const total = q.assets.length + q.photos.length + q.ops.length;

  return (
    <div className="space-y-5" data-testid="field-sync-queue">
      <div>
        <Mono className="text-gold-400 text-[0.6rem]">Offline sync</Mono>
        <h1 className="font-heading text-2xl mt-1">Sync queue</h1>
      </div>

      <div className={`flex items-center gap-2 rounded-sm px-4 py-3 border text-sm ${online ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`} data-testid="field-sync-status">
        {online ? <Cloud className="h-4 w-4" /> : <CloudOff className="h-4 w-4" />}
        {online ? "Online — changes upload automatically." : "Offline — captures are safe on this device."}
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat icon={FolderKanban} label="Assets" n={q.assets.length} />
        <Stat icon={Camera} label="Photos" n={q.photos.length} />
        <Stat icon={ListChecks} label="Updates" n={q.ops.length} />
      </div>

      {total === 0 ? (
        <div className="border border-dashed border-gold-500/25 rounded-sm p-8 text-center text-white/45 text-sm flex flex-col items-center gap-2" data-testid="field-sync-empty">
          <CheckCircle2 className="h-6 w-6 text-emerald-400" />
          Everything is synced. {lastSync && <span className="text-white/35 text-xs">Last sync {new Date(lastSync).toLocaleString()}</span>}
        </div>
      ) : (
        <div className="space-y-2" data-testid="field-sync-items">
          {q.assets.map((a) => (
            <Row key={a.id} icon={FolderKanban} title={a.name || "New asset"} sub="Asset · queued" />
          ))}
          {q.photos.map((p) => (
            <Row key={p.id} icon={Camera} title={`${p.phase} photo`} sub={p.latitude != null ? "GPS tagged · queued" : "No GPS · queued"} warn={p.latitude == null} />
          ))}
          {q.ops.map((o) => (
            <Row key={o.id} icon={ListChecks} title={o.kind.replace(/_/g, " ")} sub="Update · queued" />
          ))}
        </div>
      )}

      <Btn onClick={sync} disabled={syncing || !online} testid="field-sync-now">
        {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync now
      </Btn>
    </div>
  );
}

function Stat({ icon: Icon, label, n }) {
  return (
    <div className="border border-gold-500/20 bg-navy-900/50 rounded-sm py-3">
      <Icon className="h-4 w-4 text-gold-400 mx-auto" />
      <div className="text-lg font-heading mt-1">{n}</div>
      <Mono className="text-[0.5rem] text-white/40">{label}</Mono>
    </div>
  );
}
function Row({ icon: Icon, title, sub, warn }) {
  return (
    <div className="flex items-center gap-3 border border-gold-500/20 bg-navy-900/40 rounded-sm px-3 py-2.5">
      <Icon className="h-4 w-4 text-gold-500/70 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate capitalize">{title}</div>
        <div className={`text-[0.65rem] ${warn ? "text-red-300" : "text-white/40"}`}>{sub}</div>
      </div>
      <span className="text-amber-300 text-xs">↑</span>
    </div>
  );
}
